export const CURRENCY_CHANGE_EVENT = "yoho:currency-change"
export const CURRENCY_STORAGE_KEY = "selected-currency"
export const CURRENCY_MANUAL_OVERRIDE_KEY = "selected-currency-manual-override"

export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  JPY: "¥",
  GBP: "£",
  CNY: "¥",
  AUD: "A$",
  CAD: "C$",
  CHF: "Fr",
  HKD: "HK$",
  KRW: "₩",
  SGD: "S$",
  TWD: "NT$",
}

const LOCALE_TO_CURRENCY: Record<string, string> = {
  "zh-CN": "CNY",
  "zh-HK": "HKD",
  "zh-TW": "TWD",
  "ja-JP": "JPY",
  "ko": "KRW",
  "de-DE": "EUR",
  "fr-FR": "EUR",
  "es": "EUR",
  "it-IT": "EUR",
  "pt-PT": "EUR",
  "nl-NL": "EUR",
  "pl-PL": "EUR",
  "ro-RO": "EUR",
  "cs-CZ": "EUR",
  "hu-HU": "EUR",
  "el-GR": "EUR",
  "sv-SE": "EUR",
  "da-DK": "EUR",
  "fi-FI": "EUR",
  "hr-HR": "EUR",
  "sl-SI": "EUR",
  "sk-SK": "EUR",
  "bg-BG": "EUR",
  "et-EE": "EUR",
  "lv-LV": "EUR",
  "lt-LT": "EUR",
  "sr-Cyrl-RS": "EUR",
  "fr-CA": "CAD",
}

let cachedRates: Record<string, number> | null = null
let ratesPromise: Promise<Record<string, number>> | null = null

export function normalizeCurrencyCode(currencyCode?: string | null): string {
  const normalized = currencyCode?.trim().toUpperCase()
  return normalized || "USD"
}

export function isSupportedCurrencyCode(currencyCode?: string | null): boolean {
  return Object.prototype.hasOwnProperty.call(CURRENCY_SYMBOLS, normalizeCurrencyCode(currencyCode))
}

export function getSupportedCurrencyOrUsd(currencyCode?: string | null): string {
  const normalized = normalizeCurrencyCode(currencyCode)
  return isSupportedCurrencyCode(normalized) ? normalized : "USD"
}

export function getDefaultCurrencyForLanguage(language?: string | null): string {
  if (!language || typeof language !== "string") {
    return "USD"
  }

  if (LOCALE_TO_CURRENCY[language]) {
    return getSupportedCurrencyOrUsd(LOCALE_TO_CURRENCY[language])
  }

  const prefix = language.split("-")[0]
  const fallbackKey = Object.keys(LOCALE_TO_CURRENCY).find((key) => key.startsWith(`${prefix}-`))
  return fallbackKey ? getSupportedCurrencyOrUsd(LOCALE_TO_CURRENCY[fallbackKey]) : "USD"
}

export function normalizeCurrencyMap(input: unknown): Record<string, number> {
  if (Array.isArray(input) && input[0] && typeof input[0] === "object") {
    const firstItem = input[0] as Record<string, unknown>
    if (firstItem.rates && typeof firstItem.rates === "object") {
      return firstItem.rates as Record<string, number>
    }
    return firstItem as Record<string, number>
  }

  if (input && typeof input === "object") {
    const map = input as Record<string, unknown>
    if (map.rates && typeof map.rates === "object") {
      return map.rates as Record<string, number>
    }
    return map as Record<string, number>
  }

  return {}
}

export async function loadDisplayCurrencyRates(): Promise<Record<string, number>> {
  if (cachedRates) {
    return cachedRates
  }

  if (!ratesPromise) {
    ratesPromise = fetch("/api/display-currency", {
      cache: "no-store",
      headers: {
        accept: "application/json",
      },
    })
      .then(async (response) => {
        const json = await response.json().catch(() => null)
        if (!response.ok) {
          throw new Error(
            (json &&
            typeof json === "object" &&
            "message" in json &&
            typeof json.message === "string"
              ? json.message
              : null) || "Failed to load currency rates"
          )
        }

        cachedRates = normalizeCurrencyMap(
          json && typeof json === "object" && "data" in json
            ? (json as { data?: { allCurrencies?: unknown } }).data?.allCurrencies
            : undefined
        )
        return cachedRates
      })
      .catch((error) => {
        ratesPromise = null
        throw error
      })
  }

  return ratesPromise
}

export function resolveDisplayMoney(params: {
  amount: number
  sgAmount?: number
  selectedCurrency?: string | null
  rates?: Record<string, number> | null
}) {
  const selectedCurrency = normalizeCurrencyCode(params.selectedCurrency)
  const rates = params.rates || {}

  if (selectedCurrency === "SGD" && params.sgAmount && params.sgAmount > 0) {
    return {
      amount: params.sgAmount,
      currencyCode: "SGD",
      currencySymbol: CURRENCY_SYMBOLS.SGD,
    }
  }

  if (selectedCurrency !== "USD") {
    const exchangeRate = Number(rates[selectedCurrency])
    if (Number.isFinite(exchangeRate) && exchangeRate > 0) {
      return {
        amount: Math.round(params.amount * exchangeRate),
        currencyCode: selectedCurrency,
        currencySymbol: CURRENCY_SYMBOLS[selectedCurrency] || selectedCurrency,
      }
    }
  }

  return {
    amount: params.amount,
    currencyCode: "USD",
    currencySymbol: CURRENCY_SYMBOLS.USD,
  }
}
